import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';

const ManageRespondents = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFiscalYear, setActiveFiscalYear] = useState(null);
  const [respondentCounts, setRespondentCounts] = useState(null);
  const [quota, setQuota] = useState({ iit_quota: 0, eit1_quota: 0, eit2_quota: 0 });
  const [iitRespondents, setIitRespondents] = useState([]);
  const [eit1Respondents, setEit1Respondents] = useState([]);
  const [eit2Respondents, setEit2Respondents] = useState([]);
  const [iitQrCode, setIitQrCode] = useState('');
  const [eit1QrCode, setEit1QrCode] = useState('');
  const [eit2QrCode, setEit2QrCode] = useState('');
  const navigate = useNavigate();

  // ตรวจสอบว่าผู้ใช้มีสิทธิ์หรือไม่
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      Swal.fire({
        icon: 'error',
        title: 'ไม่มีสิทธิ์เข้าถึง',
        text: 'คุณไม่มีสิทธิ์เข้าถึงหน้านี้',
        confirmButtonText: 'ตกลง'
      });
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  // ดึงข้อมูลปีงบประมาณปัจจุบัน
  useEffect(() => {
    const fetchFiscalYear = async () => {
      try {
        const response = await api.get('/fiscal-years/active');
        setActiveFiscalYear(response.data);
      } catch (error) {
        console.error('Error fetching fiscal year:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดึงข้อมูลปีงบประมาณได้',
          confirmButtonText: 'ตกลง'
        });
      }
    };

    fetchFiscalYear();
  }, []);

  // ดึงข้อมูลจำนวนผู้ตอบแบบสอบถามและโควตา
  useEffect(() => {
    const fetchRespondentCounts = async () => {
      if (!activeFiscalYear || !user?.school_id) return;
      
      setLoading(true);
      try {
        const response = await api.get(`/respondents/count/${user.school_id}/${activeFiscalYear.id}`);
        setRespondentCounts(response.data);
        
        // ตั้งค่าโควตาจากข้อมูลที่ได้
        setQuota({
          iit_quota: response.data.iit.quota,
          eit1_quota: response.data.eit1.quota,
          eit2_quota: response.data.eit2.quota
        });
        
        setError(null);
      } catch (error) {
        console.error('Error fetching respondent counts:', error);
        setError('ไม่สามารถดึงข้อมูลผู้ตอบแบบสอบถามได้');
      } finally {
        setLoading(false);
      }
    };

    fetchRespondentCounts();
  }, [activeFiscalYear, user?.school_id]);

  // ดึงข้อมูลผู้ตอบแบบสอบถาม
  useEffect(() => {
    const fetchRespondents = async (type) => {
      if (!activeFiscalYear || !user?.school_id) return;
      
      try {
        const response = await api.get(`/respondents/${user.school_id}/${activeFiscalYear.id}/${type}`);
        
        if (type === 1) {
          setIitRespondents(response.data);
        } else if (type === 2) {
          setEit1Respondents(response.data);
        } else if (type === 3) {
          setEit2Respondents(response.data);
        }
      } catch (error) {
        console.error(`Error fetching type ${type} respondents:`, error);
        // ไม่ต้องแสดง error เพราะอาจจะยังไม่มีข้อมูล
      }
    };

    if (!loading && !error) {
      fetchRespondents(1); // IIT
      fetchRespondents(2); // EIT1
      fetchRespondents(3); // EIT2
    }
  }, [activeFiscalYear, user?.school_id, loading, error]);

  // สร้าง QR Code สำหรับแบบสอบถาม
  useEffect(() => {
    const generateQRCode = async (type) => {
      if (!activeFiscalYear || !user?.school_id) return;
      
      try {
        const response = await api.post('/generate-qr', {
          school_id: user.school_id,
          fiscal_year_id: activeFiscalYear.id,
          questionnaire_type_id: type
        });
        
        if (type === 1) {
          setIitQrCode(response.data.qrCode);
        } else if (type === 2) {
          setEit1QrCode(response.data.qrCode);
        } else if (type === 3) {
          setEit2QrCode(response.data.qrCode);
        }
      } catch (error) {
        console.error(`Error generating QR code for type ${type}:`, error);
      }
    };

    if (activeFiscalYear && user?.school_id) {
      generateQRCode(1); // IIT
      generateQRCode(2); // EIT1
      generateQRCode(3); // EIT2
    }
  }, [activeFiscalYear, user?.school_id]);

  // บันทึกโควตา
  const handleSaveQuota = async () => {
    if (!activeFiscalYear || !user?.school_id) return;
    
    try {
      await api.post('/quotas', {
        school_id: user.school_id,
        fiscal_year_id: activeFiscalYear.id,
        iit_quota: quota.iit_quota,
        eit1_quota: quota.eit1_quota,
        eit2_quota: quota.eit2_quota
      });
      
      Swal.fire({
        icon: 'success',
        title: 'บันทึกสำเร็จ',
        text: 'บันทึกโควตาผู้ตอบแบบสอบถามเรียบร้อยแล้ว',
        confirmButtonText: 'ตกลง'
      });
      
      // รีโหลดข้อมูล
      window.location.reload();
    } catch (error) {
      console.error('Error saving quota:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกโควตาได้',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  if (loading) {
    return (
      <div className="wrapper">
        <Header user={user} title="จัดการผู้ตอบแบบสอบถาม" />
        <Sidebar user={user} />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0">จัดการผู้ตอบแบบสอบถาม</h1>
                </div>
              </div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">กำลังโหลด...</span>
                </div>
                <p className="mt-2">กำลังโหลดข้อมูล...</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="wrapper">
        <Header user={user} title="จัดการผู้ตอบแบบสอบถาม" />
        <Sidebar user={user} />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0">จัดการผู้ตอบแบบสอบถาม</h1>
                </div>
              </div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              <div className="alert alert-danger">
                <h5><i className="fas fa-exclamation-triangle"></i> เกิดข้อผิดพลาด!</h5>
                <p>{error}</p>
                <button 
                  className="btn btn-outline-danger"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-sync"></i> ลองใหม่
                </button>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการผู้ตอบแบบสอบถาม" />
      <Sidebar user={user} />
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">จัดการผู้ตอบแบบสอบถาม</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item"><a href="/admin/dashboard">หน้าหลัก</a></li>
                  <li className="breadcrumb-item active">จัดการผู้ตอบแบบสอบถาม</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {/* ตั้งค่าโควตา */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">กำหนดจำนวนผู้ตอบแบบสอบถาม</h3>
              </div>
              <div className="card-body">
                <form onSubmit={(e) => { e.preventDefault(); handleSaveQuota(); }}>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="iit_quota">จำนวนผู้ตอบแบบสอบถาม IIT (บุคลากรภายใน)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="iit_quota"
                          value={quota.iit_quota}
                          onChange={(e) => setQuota({...quota, iit_quota: parseInt(e.target.value)})}
                          min="0"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="eit1_quota">จำนวนผู้ตอบแบบสอบถาม EIT1 (ผู้ใช้บริการโรงเรียนกำหนด)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="eit1_quota"
                          value={quota.eit1_quota}
                          onChange={(e) => setQuota({...quota, eit1_quota: parseInt(e.target.value)})}
                          min="0"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="eit2_quota">จำนวนผู้ตอบแบบสอบถาม EIT2 (ผู้ใช้บริการทั่วไป)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="eit2_quota"
                          value={quota.eit2_quota}
                          onChange={(e) => setQuota({...quota, eit2_quota: parseInt(e.target.value)})}
                          min="0"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save"></i> บันทึก
                  </button>
                </form>
              </div>
            </div>
            
            {/* แสดงสถานะการตอบแบบสอบถาม */}
            {respondentCounts && (
              <div className="row">
                <div className="col-lg-4">
                  <div className="card card-primary">
                    <div className="card-header">
                      <h3 className="card-title">แบบสอบถาม IIT (บุคลากรภายใน)</h3>
                    </div>
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <h4>สถานะการตอบแบบสอบถาม</h4>
                        <h2>{respondentCounts.iit.current} / {respondentCounts.iit.quota}</h2>
                        <div className="progress">
                          <div 
                            className="progress-bar bg-primary" 
                            role="progressbar" 
                            style={{ width: `${respondentCounts.iit.quota > 0 ? (respondentCounts.iit.current / respondentCounts.iit.quota) * 100 : 0}%` }} 
                            aria-valuenow={respondentCounts.iit.current} 
                            aria-valuemin="0" 
                            aria-valuemax={respondentCounts.iit.quota}
                          ></div>
                        </div>
                      </div>
                      
                      {iitQrCode && (
                        <div className="text-center mt-4">
                          <h5>QR Code สำหรับตอบแบบสอบถาม</h5>
                          <img src={iitQrCode} alt="IIT QR Code" className="img-fluid" style={{ maxWidth: '200px' }} />
                        </div>
                      )}
                      
                      <div className="mt-4">
                        <h5>รายชื่อผู้ตอบแบบสอบถาม</h5>
                        {iitRespondents.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>ชื่อ-นามสกุล</th>
                                  <th>เบอร์โทร</th>
                                  <th>อีเมล</th>
                                </tr>
                              </thead>
                              <tbody>
                                {iitRespondents.map((respondent, index) => (
                                  <tr key={respondent.id}>
                                    <td>{index + 1}</td>
                                    <td>{respondent.name}</td>
                                    <td>{respondent.phone}</td>
                                    <td>{respondent.email}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted">ยังไม่มีผู้ตอบแบบสอบถาม</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-4">
                  <div className="card card-success">
                    <div className="card-header">
                      <h3 className="card-title">แบบสอบถาม EIT1 (โรงเรียนกำหนด)</h3>
                    </div>
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <h4>สถานะการตอบแบบสอบถาม</h4>
                        <h2>{respondentCounts.eit1.current} / {respondentCounts.eit1.quota}</h2>
                        <div className="progress">
                          <div 
                            className="progress-bar bg-success" 
                            role="progressbar" 
                            style={{ width: `${respondentCounts.eit1.quota > 0 ? (respondentCounts.eit1.current / respondentCounts.eit1.quota) * 100 : 0}%` }} 
                            aria-valuenow={respondentCounts.eit1.current} 
                            aria-valuemin="0" 
                            aria-valuemax={respondentCounts.eit1.quota}
                          ></div>
                        </div>
                      </div>
                      
                      {eit1QrCode && (
                        <div className="text-center mt-4">
                          <h5>QR Code สำหรับตอบแบบสอบถาม</h5>
                          <img src={eit1QrCode} alt="EIT1 QR Code" className="img-fluid" style={{ maxWidth: '200px' }} />
                        </div>
                      )}
                      
                      <div className="mt-4">
                        <h5>รายชื่อผู้ตอบแบบสอบถาม</h5>
                        {eit1Respondents.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>ชื่อ-นามสกุล</th>
                                  <th>เบอร์โทร</th>
                                  <th>อีเมล</th>
                                </tr>
                              </thead>
                              <tbody>
                                {eit1Respondents.map((respondent, index) => (
                                  <tr key={respondent.id}>
                                    <td>{index + 1}</td>
                                    <td>{respondent.name}</td>
                                    <td>{respondent.phone}</td>
                                    <td>{respondent.email}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted">ยังไม่มีผู้ตอบแบบสอบถาม</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-4">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">แบบสอบถาม EIT2 (บุคคลทั่วไป)</h3>
                    </div>
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <h4>สถานะการตอบแบบสอบถาม</h4>
                        <h2>{respondentCounts.eit2.current} / {respondentCounts.eit2.quota}</h2>
                        <div className="progress">
                          <div 
                            className="progress-bar bg-info" 
                            role="progressbar" 
                            style={{ width: `${respondentCounts.eit2.quota > 0 ? (respondentCounts.eit2.current / respondentCounts.eit2.quota) * 100 : 0}%` }} 
                            aria-valuenow={respondentCounts.eit2.current} 
                            aria-valuemin="0" 
                            aria-valuemax={respondentCounts.eit2.quota}
                          ></div>
                        </div>
                      </div>
                      
                      {eit2QrCode && (
                        <div className="text-center mt-4">
                          <h5>QR Code สำหรับตอบแบบสอบถาม</h5>
                          <img src={eit2QrCode} alt="EIT2 QR Code" className="img-fluid" style={{ maxWidth: '200px' }} />
                        </div>
                      )}
                      
                      {/* <div className="mt-4">
                        <h5>รายชื่อผู้ตอบแบบสอบถาม</h5>
                        {eit2Respondents.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>ชื่อ-นามสกุล</th>
                                  <th>เบอร์โทร</th>
                                  <th>อีเมล</th>
                                </tr>
                              </thead>
                              <tbody>
                                {eit2Respondents.map((respondent, index) => (
                                  <tr key={respondent.id}>
                                    <td>{index + 1}</td>
                                    <td>{respondent.name}</td>
                                    <td>{respondent.phone}</td>
                                    <td>{respondent.email}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted">ยังไม่มีผู้ตอบแบบสอบถาม</p>
                        )}
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default ManageRespondents;